import React, { ChangeEvent } from 'react';
import { FormControl } from 'components/Form';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'components/Modal';
import TextArea from 'components/TextArea/TextArea';
import TextInput from 'components/TextInput/TextInput';
import { useAppDispatch, useAppSelector } from 'redux/store';
import {
  resetJetPayConfigOptions,
  resetJetPayConfigOptionsValidation,
  updateJetpayConfigOptions,
  validateJetPayConfigOptions,
} from 'admin/companies/CompanySettingsPage/payment/jetpay/redux/slice';
import styles from './CreateEditConfigModal.module.scss';
import { createJetpayCompanyConfigThunk, updateJetpayCompanyConfigThunk } from '../../redux/thunk';
import { toNumber } from 'utils/numberUtils/toNumber';
import { ContextParams } from 'types/ContextParams.interface';
import { useParams } from 'react-router-dom';
import { compositeStyles } from 'utils/cssUtils';

const validationMessage = 'Required Field.';

export const CreateEditConfigModal = ({ isNew, onClose }: { isNew: boolean; onClose: () => void }) => {
  const {
    validation,
    companyPaymentConfig,
    companyPaymentConfig: {
      options: { name: configName, clientXmlPublicKey, companyNumber, serverPublicKey },
    },
    isSaving,
  } = useAppSelector((state) => state.companies.companySettings.paymentProcessing.jetpay);
  const dispatch = useAppDispatch();
  const { contextId: companyId } = useParams<ContextParams>();

  const headerLabel = `${isNew ? 'New' : 'Edit'} Settings Configuration`;
  const modalFooterPrimaryButton = isNew ? 'Create Configuration' : 'Save Changes';

  const submitModal = () => {
    dispatch(resetJetPayConfigOptionsValidation());
    dispatch(validateJetPayConfigOptions());
  };

  const cancelChanges = () => {
    dispatch(resetJetPayConfigOptionsValidation());
    dispatch(resetJetPayConfigOptions());
  };

  const createUpdateConfigRequest = React.useCallback(async () => {
    if (validation.hasPassedValidation) {
      if (isNew) {
        await dispatch(createJetpayCompanyConfigThunk({ companyId, config: companyPaymentConfig }));
      } else {
        await dispatch(updateJetpayCompanyConfigThunk({ companyId, config: companyPaymentConfig }));
      }
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isNew, validation.hasPassedValidation]);

  React.useEffect(() => {
    createUpdateConfigRequest();
  }, [createUpdateConfigRequest]);

  return (
    <Modal setShowModal={() => {}} loading={false}>
      <ModalHeader headerLabel={headerLabel} setShowModal={() => {}} />
      <ModalBody>
        <FormControl
          label='Configuration Name *'
          bold
          errorMessage={validation.configName.hasError ? validationMessage : ''}
          withError={validation.configName.hasError}
        >
          <TextInput
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              dispatch(updateJetpayConfigOptions({ name: e.target.value.toString() }));
            }}
            placeholder={'e.g. Central Region'}
            value={configName || ''}
            withError={validation.configName.hasError}
          />
        </FormControl>

        <FormControl
          label='Company Number *'
          bold
          errorMessage={validation.companyNumber.hasError ? validationMessage : ''}
          withError={validation.companyNumber.hasError}
        >
          <TextInput
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              dispatch(updateJetpayConfigOptions({ companyNumber: toNumber(e.target.value) }));
            }}
            placeholder={'Enter Company Number'}
            value={companyNumber || ''}
            withError={validation.companyNumber.hasError}
            type='number'
          />
        </FormControl>

        <FormControl label='Client Public Key' bold>
          <div className={compositeStyles([styles.clientPublicKey, { disabled: isNew }], styles)}>
            {isNew ? 'This key will be auto-generated upon creation of this configuration.' : clientXmlPublicKey}
          </div>
        </FormControl>

        <FormControl
          label='Server Public Key  '
          bold
          errorMessage={validation.serverPublicKey.hasError ? validationMessage : ''}
          withError={validation.serverPublicKey.hasError}
          disabled={isNew}
        >
          <div className={styles.serverPublicKey}>
            To obtain this public key, send JetPay the auto-generated Client Public Key above. They will send back the
            Server Public key which can be entered here.
          </div>
          <TextArea
            disabled={isNew}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              dispatch(
                updateJetpayConfigOptions({
                  serverPublicKey: e.target.value.toString(),
                })
              );
            }}
            placeholder={'Enter Server Public Key'}
            value={serverPublicKey || ''}
            withError={validation.serverPublicKey.hasError}
          />
        </FormControl>
      </ModalBody>

      <ModalFooter
        primaryLabel={modalFooterPrimaryButton}
        primaryActionHandler={submitModal}
        loading={isSaving}
        secondaryLabel='Cancel'
        secondaryActionHandler={() => {
          cancelChanges();
          onClose();
        }}
      />
    </Modal>
  );
};
